import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { JS0N } from './ui/LoadingIndicator';
import { Alert, Card, Button, Container, Row, Col } from 'react-bootstrap';
import { useTheme } from '../ThemeContext';
import useSound from 'use-sound';
import { debounce } from 'lodash';
import { useFilter } from './FilterContext';
import { WS_BASE_URL } from '../config/runtime';

const WebSocketContext = createContext(null);

const calculateBenefit = (fp, p) => Math.round((fp / p) * 100);

const filterSales = (sales, filters) => {
  return sales.filter(sale => {
    // 1. Поиск фильтра
    const f = filters.find(filter =>
      (sale.marketplace === 'wb' && filter.categ === sale.subject) ||
      (['ozon', 'yandex', 'detmir'].includes(sale.marketplace) &&
        filter.marketplace === sale.marketplace)
    );
    if (!f || !f.status_categ) return false;

    // 2. Расчёт выгоды от отзывов
    const fp = sale.feedbackpoints ?? 0;
    const p = sale.price;
    const benefitPercent = fp > 0 ? calculateBenefit(fp, p) : 0;

    /* 3. «отзывной» блок — если >= 50 % */

    if (fp > 0 && benefitPercent >= 50) {
      if (f.status_feedbacks) {
        /* 3.а. нужно ещё пройти пользовательский порог */
        if (benefitPercent < f.feedbackminpercent) return false;

        /* 3.б. возвратность */
        if (sale.isunrefusable && !f.unrefusableallowed) return false;

        /* 3.в. остаток и склад (WB) */
        if (sale.marketplace === 'wb') {
          if (sale.col < f.ostatok) return false;
          const sklad = sale.sklad2 ?? sale.sklad ?? '';
          const hasWB = /WB - (\d+)шт\./.test(sklad);
          const hasPR = /ПРОД - (\d+)шт\./.test(sklad);
          if ((!hasWB && hasPR && !f.prod) || (!hasWB && !hasPR)) return false;
        }
        /* ничего не проверяем по скидке/AI */
        return true;
      }
    }


    // 4. Обычная фильтрация по скидке
    let isDiscountMatch = false;
    if (sale.marketplace === 'wb') {
      isDiscountMatch = f.sale === 0
        ? true
        : sale.diapazmin && sale.price <= sale.diapazmin * (1 - f.sale / 100);
    } else {
      isDiscountMatch = f.sale === 0
        ? true
        : sale.oldprice && sale.price <= sale.oldprice * (1 - f.sale / 100);
    }
    if (!isDiscountMatch) return false;

    // 5. Доп. проверки для WB: AI, остаток, склад
    if (sale.marketplace === 'wb') {
      if (sale.ai < f.ai) return false;
      if (sale.col < f.ostatok) return false;
      const sklad = sale.sklad || '';
      const hasWB = /WB - (\d+)шт\./.test(sklad);
      const hasPR = /ПРОД - (\d+)шт\./.test(sklad);
      if ((!hasWB && hasPR && !f.prod) || (!hasWB && !hasPR)) return false;
    }

    return true;
  });
};
export const WebSocketProvider = ({ children }) => {
  const { theme } = useTheme();
  const { token, userInfo, isSoundEnabled, setIsLoading, logout, getFilters, filters, getFeedbackSales, getWsTicket } = useAuth();
  const { showFeedbackOnly } = useFilter();
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const playSoundRef = useRef(null);
  const [isConnected, setIsConnected] = useState(null);
  const [shouldReconnect, setShouldReconnect] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [subscriptionEnded, setSubscriptionEnded] = useState(false);
  const isFetchingRef = useRef(false);
  const [sales, setSales] = useState([]);
  const [feedbackSales, setFeedbackSales] = useState([]);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const feedbackFetchingRef = useRef(false);
  const [play] = useSound('/notifsound.mp3');
  const salesRef = useRef([]);
  const filtersLoaded = useRef(false);

  useEffect(() => {
    salesRef.current = sales;
  }, [sales]);

  useEffect(() => {
    playSoundRef.current = play;
  }, [play]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        await getFilters();  // Загружаем фильтры
        filtersLoaded.current = true;
        console.log('Фильтры успешно загружены:', filters);  // Убедимся, что фильтры загрузились
      } catch (error) {
        console.error('Ошибка при получении фильтров:', error);
      }
    };

    fetchFilters();
  }, []);

  useEffect(() => {
    const connectWebSocket = async () => {
      if (!token || !userInfo?.status || !filtersLoaded.current) {
        console.log('Нет токена, информации о пользователе или фильтров.');
        return;
      }

      if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
        console.log('WebSocket already open or connecting.');
        return;
      }

      let wsUrl = '';
      try {
        const ticketResp = await getWsTicket();
        const ticket = String(ticketResp?.ticket || '').trim();
        if (ticket) {
          wsUrl = `${WS_BASE_URL}/ws/pwa?ticket=${encodeURIComponent(ticket)}`;
        }
      } catch (ticketError) {
        console.warn('WS ticket unavailable, fallback to legacy auth:', ticketError);
      }

      if (!wsUrl) {
        const mishura = encodeURIComponent(JS0N.stringify(token));
        const accountId = encodeURIComponent(String(userInfo.user_id));
        wsUrl = `${WS_BASE_URL}/ws/pwa?mishura=${mishura}&user_id=${accountId}`;
      }

      if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
        console.log('WebSocket already open or connecting.');
        return;
      }

      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log(`WebSocket connected`);

        setIsConnected(true);
        setShouldReconnect(true);
        setConnectionStatus('connected');
        setSubscriptionEnded(false);

        const lastSale = salesRef.current[salesRef.current.length - 1];
        const lastSaleId = lastSale?.id;

        if (lastSaleId) {
          console.log(`Requesting new sales from ${lastSaleId}`);
          ws.current.send(JSON.stringify({ type: 'getNewSales', id: lastSaleId }));
        } else {
          console.log('Requesting last sales');
          ws.current.send(JSON.stringify({ type: 'getLastSales' }));
        }

      };

      ws.current.onmessage = (message) => {
        const data = JSON.parse(message.data);

        if (data.type === 'sale' && data.dataApp) {
          console.log(data);
          const filtered = filterSales(data.dataApp, filters);

          setSales((prevSales) => [...prevSales, ...filtered]);

          if (isSoundEnabled && filtered.length > 0) playSoundRef.current();

        } else if (data.type === 'lastSales') {
          console.log(`lastSales ${data.sales.length}`);
          setSales(filterSales(data.sales, filters));

        } else if (data.type === 'newSales') {

          console.log(`newSales ${data.sales.length}`);
          const filtered = filterSales(data.sales, filters);
          setSales(prev => [...prev, ...filtered]);
        }
        else if (data.type === 'oldSales') {
          const filtered = filterSales(data.sales, filters);
          setSales(prev => [...filtered, ...prev]);

        } else if (data.type === 'remaining') {
          console.log(data.dataApp);
          setSales((prevSales) => {
            const remainingDataMap = new Map(data.dataApp.map(item => [item[0], { col2: item[1], sklad2: item[2], sizes: item[3] }]));
            return prevSales.map(sale => {
              if (remainingDataMap.has(sale.id)) {
                const { col2, sklad2, sizes } = remainingDataMap.get(sale.id);
                return { ...sale, col2, sklad2, sizes };
              }
              return sale;
            });
          });
        } else if (data.type === 'new_price') {
          console.log(data.dataApp);
          setSales((prevSales) => {
            const newPriceDataMap = new Map(data.dataApp.map(item => [item[0], { price2: item[1] }]));
            return prevSales.map(sale => {
              if (newPriceDataMap.has(sale.id)) {
                const { price2 } = newPriceDataMap.get(sale.id);
                return { ...sale, price2 };
              }
              return sale;
            });
          });
        } else if (data.type === 'new_feed') {
          console.log(data.dataApp);
          setSales((prevSales) => {
            const newPriceDataMap = new Map(data.dataApp.map(item => [item[0], { feedbackpoints2: item[1] }]));
            return prevSales.map(sale => {
              if (newPriceDataMap.has(sale.id)) {
                const { feedbackpoints2 } = newPriceDataMap.get(sale.id);
                return { ...sale, feedbackpoints2 };
              }
              return sale;
            });
          });
        }
      };


      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected.', event);
        setIsConnected(false);
        setConnectionStatus('disconnected');
        //setSales([]);

        if (event.code === 1008 && event.reason === 'subscription ended') {
          console.log('Subscription ended.');
          setSubscriptionEnded(true);
          setShouldReconnect(false);

        } else if (event.code === 1008 && event.reason === 'logged in to another device') {
          setShouldReconnect(false);
          logout()

        } else if (shouldReconnect && !document.hidden && !event.wasClean) {
          console.log('Attempting to reconnect...');
          reconnectTimeout.current = setTimeout(() => {
            void connectWebSocket();
          }, 1000);
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        ws.current.close();
        setIsConnected(false);
        setConnectionStatus('error');
      };
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (ws.current) {
          console.log('Document hidden, closing WebSocket.');
          ws.current.close();
        }
        setIsConnected(false);
        setConnectionStatus('disconnected');
      } else {
        console.log('Document visible, connecting WebSocket.');
        void connectWebSocket();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    void connectWebSocket();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setShouldReconnect(false);
      clearTimeout(reconnectTimeout.current);
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [filters, token, userInfo]);

  useEffect(() => {
    const loadFeedback = async () => {
      if (!showFeedbackOnly || !token || !userInfo?.status) return;
      setIsLoadingFeedback(true);
      try {
        const list = await getFeedbackSales({ limit: 500 });
        setFeedbackSales((list || []).slice().reverse());
      } catch (err) {
        console.error('Error loading feedback sales:', err);
      } finally {
        setIsLoadingFeedback(false);
      }
    };

    loadFeedback();
  }, [showFeedbackOnly, token, userInfo, getFeedbackSales]);

  const fetchMoreSales = (firstId) => {
    return new Promise((resolve, reject) => {
      if (isFetchingRef.current) {
        reject('Already fetching');
        return;
      }

      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        isFetchingRef.current = true;
        ws.current.send(JSON.stringify({ type: 'getOldSales', id: firstId }));
        resolve();
      } else {
        reject('WebSocket is not open');
      }
    }).finally(() => {
      isFetchingRef.current = false;
    });
  };

  const fetchMoreFeedbackSales = (firstId) => {
    return new Promise(async (resolve, reject) => {
      if (!firstId) {
        resolve();
        return;
      }

      if (feedbackFetchingRef.current) {
        reject('Already fetching feedback sales');
        return;
      }

      feedbackFetchingRef.current = true;
      try {
        const older = await getFeedbackSales({ beforeId: firstId, limit: 300 });
        const olderAsc = (older || []).slice().reverse();
        if (older && older.length > 0) {
          setFeedbackSales(prev => [...olderAsc, ...prev]);
        }
        resolve();
      } catch (err) {
        reject(err);
      } finally {
        feedbackFetchingRef.current = false;
      }
    });
  };


  useEffect(() => {
    const loadCachedData = async () => {
      const savedSales = localStorage.getItem('cachedSales');
      if (!savedSales) return;
      if (!filters) return;
      try {
        const parsedSales = JSON.parse(savedSales);
        console.log('Loaded from cache:', parsedSales.length);

        // Ждем загрузки фильтров перед фильтрацией
        while (!filtersLoaded.current) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        const filtered = filterSales(parsedSales, filters);
        setSales(filtered.slice(-1000));
      } catch (error) {
        console.error('Error parsing cached sales:', error);
      }
    };

    loadCachedData();
  }, [filters]); // Зависимость от filters

  useEffect(() => {
    const saveSales = () => {
      if (sales.length > 0) {
        const cachedSales = sales.slice(-1000);
        localStorage.setItem('cachedSales', JSON.stringify(cachedSales));
      }
    };
    console.log(`sales length: ${sales.length}`);
    // Дебаунс на 1 секунду
    const saveDebounced = debounce(saveSales, 1000);

    // Сохраняем при изменении sales
    saveDebounced();

    // Отменяем отложенный вызов при размонтировании
    return () => saveDebounced.cancel();
  }, [sales]);

  return (
    <WebSocketContext.Provider value={{
      ws: ws.current,
      isConnected,
      connectionStatus,
      subscriptionEnded,
      sales,
      feedbackSales,
      fetchMoreSales,
      fetchMoreFeedbackSales,
      isLoadingFeedback
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);

const ConnectionStatus = () => {
  const { theme } = useTheme();
  const { isConnected, connectionStatus, subscriptionEnded } = useWebSocket();
  const { userInfo } = useAuth();

  const themeClass = theme === 'dark' ? 'text-white bg-dark' : 'text-dark bg-light';
  const headerStyle = {
    borderRadius: '10px',
    padding: '20px',
    margin: '10px',
    backgroundColor: theme === 'dark' ? '#212529' : '#f8f9fa',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  };

  return (
    <>
      {!isConnected && userInfo?.status && !subscriptionEnded && (
        <Alert variant="warning" className="text-center connection-alert" style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1050,
          minWidth: '300px',
          maxWidth: '90%',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          Устанавливаем связь с космосом...
        </Alert>
      )}
      {subscriptionEnded && (
        <Container className={`pt-3 pb-5 ${themeClass}`}>
          <Row className="mb-3">
            <Col>
              <Card className={`d-flex align-items-center justify-content-center text-center ${themeClass} shadow-lg`} style={headerStyle}>
                <Card.Body>
                  <Card.Title>Подписка закончилась</Card.Title>
                  <Card.Text>Пожалуйста, оформите подписку, чтобы продолжить получать скидки.</Card.Text>
                  <Button onClick={() => window.location.href = '/subscribe'} variant="primary">
                    Оплатить подписку
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
      {!userInfo?.status && !subscriptionEnded && (
        <Container className={`pt-3 pb-5 ${themeClass}`}>
          <Row className="mb-3">
            <Col>
              <Card className={`d-flex align-items-center justify-content-center text-center ${themeClass} shadow-lg`} style={headerStyle}>
                <Card.Body>
                  <Card.Title>У вас неактивна подписка</Card.Title>
                  <Card.Text>Пожалуйста, оформите подписку, чтобы продолжить получать скидки.</Card.Text>
                  <Button onClick={() => window.location.href = '/subscribe'} variant="primary">
                    Оплатить подписку
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
};

export default ConnectionStatus;
